import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/common/Button';
import { listWorkflowStages, listWorkflowTemplates } from '../../services/workflow';
import { alSelClass } from '../../utils/alSelClass';

export default function EscalationRulesPage() {
  const { t } = useTranslation();
  const templates = listWorkflowTemplates();
  const [templateId, setTemplateId] = useState(templates[0]?.id ?? '');
  const stages = useMemo(() => listWorkflowStages(templateId), [templateId]);
  const [stageId, setStageId] = useState(stages[0]?.id ?? '');

  const selectedTemplate = templates.find((t) => t.id === templateId);
  const selectedStage = stages.find((s) => s.id === stageId);

  const [reminderRecipient, setReminderRecipient] = useState('Assigned User');
  const [lvl1UsersOn, setLvl1UsersOn] = useState(true);
  const [lvl1RolesOn, setLvl1RolesOn] = useState(true);
  const [lvl2UsersOn, setLvl2UsersOn] = useState(true);
  const [lvl2RolesOn, setLvl2RolesOn] = useState(true);

  const [lvl1Users, setLvl1Users] = useState('');
  const [lvl1Roles, setLvl1Roles] = useState('');
  const [lvl2Users, setLvl2Users] = useState('');
  const [lvl2Roles, setLvl2Roles] = useState('');

  const [sysNoti, setSysNoti] = useState(true);
  const [emailNoti, setEmailNoti] = useState(true);

  return (
    <>
      <div className="bc">
        <span>{t('workflow.management')}</span>
        <span className="bc-sep">/</span>{t('workflow.escalationRules.breadcrumb')}
      </div>

      <div className="page-hdr" style={{ marginBottom: 12 }}>
        <div>
          <div className="pg-title" style={{ marginBottom: 0 }}>
            {t('workflow.escalationRules.title')}
          </div>
        </div>
      </div>

      <div className="al-filter" style={{ marginBottom: 14 }}>
        <div className="al-filter-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <div className="al-f">
            <div className="al-l">{t('workflow.escalationRules.workflowTemplateName')}</div>
            <input className="al-inp" value={selectedTemplate?.name ?? ''} readOnly />
          </div>
          <div className="al-f">
            <div className="al-l">{t('workflow.escalationRules.workflowStage')}</div>
            <input className="al-inp" value={selectedStage?.code ?? selectedStage?.label ?? ''} readOnly />
          </div>
        </div>
      </div>

      <div className="al-filter">
        <div className="al-filter-title" style={{ marginBottom: 12 }}>
          {t('workflow.escalationRules.hierarchyTitle')}
        </div>

        <div className="esc-wrap">
          <div className="esc-line" aria-hidden="true" />

          <div className="esc-row">
            <div className="esc-ico base" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v4" />
                <path d="M12 16h.01" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div className="esc-title">{t('workflow.escalationRules.reminderRecipient')}</div>
              <select className={alSelClass(reminderRecipient)} value={reminderRecipient} onChange={(e) => setReminderRecipient(e.target.value)}>
                <option>Assigned User</option>
                <option>Supervisor / Role</option>
                <option>Head of Division / Role</option>
              </select>
            </div>
          </div>

          <div className="esc-row">
            <div className="esc-ico lvl1" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="9" />
                <path d="M8 12h8" />
                <path d="M12 8v8" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div className="esc-title">{t('workflow.escalationRules.escalationLevel1')}</div>

              <label className="ccr-chk" style={{ marginBottom: 8 }}>
                <input type="checkbox" checked={lvl1UsersOn} onChange={(e) => setLvl1UsersOn(e.target.checked)} />
                <span>{t('workflow.escalationRules.selectUsers')}</span>
              </label>
              <select className={alSelClass(lvl1Users)} value={lvl1Users} onChange={(e) => setLvl1Users(e.target.value)} disabled={!lvl1UsersOn}>
                <option value="" disabled hidden>
                  {t('workflow.escalationRules.selectUsers')}
                </option>
                <option>Mr. Nimal Silva</option>
                <option>Ms. K. Perera</option>
                <option>Mr. Kasun Bandara</option>
              </select>

              <div style={{ height: 10 }} />

              <label className="ccr-chk" style={{ marginBottom: 8 }}>
                <input type="checkbox" checked={lvl1RolesOn} onChange={(e) => setLvl1RolesOn(e.target.checked)} />
                <span>{t('workflow.escalationRules.selectUserRoles')}</span>
              </label>
              <select className={alSelClass(lvl1Roles)} value={lvl1Roles} onChange={(e) => setLvl1Roles(e.target.value)} disabled={!lvl1RolesOn}>
                <option value="" disabled hidden>
                  {t('workflow.escalationRules.selectUserRoles')}
                </option>
                <option>Senior Legal Officer</option>
                <option>System Administrator</option>
                <option>Supervising Officer</option>
              </select>
            </div>
          </div>

          <div className="esc-row">
            <div className="esc-ico lvl2" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div className="esc-title">{t('workflow.escalationRules.escalationLevel2')}</div>

              <label className="ccr-chk" style={{ marginBottom: 8 }}>
                <input type="checkbox" checked={lvl2UsersOn} onChange={(e) => setLvl2UsersOn(e.target.checked)} />
                <span>{t('workflow.escalationRules.selectUsers')}</span>
              </label>
              <select className={alSelClass(lvl2Users)} value={lvl2Users} onChange={(e) => setLvl2Users(e.target.value)} disabled={!lvl2UsersOn}>
                <option value="" disabled hidden>
                  {t('workflow.escalationRules.selectUsers')}
                </option>
                <option>Deputy Solicitor General</option>
                <option>Director General</option>
              </select>

              <div style={{ height: 10 }} />

              <label className="ccr-chk" style={{ marginBottom: 8 }}>
                <input type="checkbox" checked={lvl2RolesOn} onChange={(e) => setLvl2RolesOn(e.target.checked)} />
                <span>{t('workflow.escalationRules.selectUserRoles')}</span>
              </label>
              <select className={alSelClass(lvl2Roles)} value={lvl2Roles} onChange={(e) => setLvl2Roles(e.target.value)} disabled={!lvl2RolesOn}>
                <option value="" disabled hidden>
                  {t('workflow.escalationRules.selectUserRoles')}
                </option>
                <option>Head of Division</option>
                <option>Director General</option>
              </select>
            </div>
          </div>
        </div>

        <div style={{ height: 14 }} />

        <div className="al-filter-title" style={{ marginBottom: 10 }}>
          {t('workflow.escalationRules.notificationOptions')}
        </div>
        <label className="ccr-chk" style={{ marginBottom: 8 }}>
          <input type="checkbox" checked={sysNoti} onChange={(e) => setSysNoti(e.target.checked)} />
          <span>{t('workflow.escalationRules.systemNotification')}</span>
        </label>
        <label className="ccr-chk" style={{ marginBottom: 0 }}>
          <input type="checkbox" checked={emailNoti} onChange={(e) => setEmailNoti(e.target.checked)} />
          <span>{t('workflow.escalationRules.emailNotification')}</span>
        </label>

        <div className="al-filter-actions">
          <Button onClick={() => null}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={() => null}>{t('common.save')}</Button>
        </div>
      </div>
    </>
  );
}
